import React, { useState, useRef, useEffect } from 'react';

interface SimpleTimePickerProps {
  value: string; // Time in HH:mm format (24 hour)
  onChange: (time: string) => void;
  className?: string;
}

export default function SimpleTimePicker({ value, onChange, className = '' }: SimpleTimePickerProps) {
  // Parse the time value
  const [hours, minutes] = value ? value.split(':').map(Number) : [14, 0]; // Default to 2:00 PM
  const [displayHour, setDisplayHour] = useState(hours > 12 ? hours - 12 : hours === 0 ? 12 : hours);
  const [displayMinute, setDisplayMinute] = useState(minutes);
  const [period, setPeriod] = useState(hours >= 12 ? 'PM' : 'AM');

  // Update when value prop changes
  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':').map(Number);
      setDisplayHour(h > 12 ? h - 12 : h === 0 ? 12 : h);
      setDisplayMinute(m);
      setPeriod(h >= 12 ? 'PM' : 'AM');
    }
  }, [value]);

  const updateTime = (newHour: number, newMinute: number, newPeriod: string) => {
    let hour24 = newHour;
    if (newPeriod === 'PM' && newHour !== 12) {
      hour24 = newHour + 12;
    } else if (newPeriod === 'AM' && newHour === 12) {
      hour24 = 0;
    }
    
    const timeString = `${hour24.toString().padStart(2, '0')}:${newMinute.toString().padStart(2, '0')}`;
    onChange(timeString);
  };

  const handleHourChange = (newHour: number) => {
    setDisplayHour(newHour);
    updateTime(newHour, displayMinute, period);
  };

  const handleMinuteChange = (newMinute: number) => {
    setDisplayMinute(newMinute);
    updateTime(displayHour, newMinute, period);
  };

  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
    updateTime(displayHour, displayMinute, newPeriod);
  };

  return (
    <div className={`flex items-center justify-center space-x-2 p-4 ${className}`}>
      {/* Hour Picker */}
      <div className="flex flex-col items-center">
        <div className="text-xs text-text-secondary mb-2">Hour</div>
        <div className="relative">
          <select
            value={displayHour}
            onChange={(e) => handleHourChange(Number(e.target.value))}
            className="appearance-none bg-transparent text-2xl font-light text-center w-16 focus:outline-none cursor-pointer"
            style={{ textAlignLast: 'center' }}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map(hour => (
              <option key={hour} value={hour}>{hour}</option>
            ))}
          </select>
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="border-t border-b border-primary-200 w-full h-8 absolute"></div>
          </div>
        </div>
      </div>

      <div className="text-2xl font-light text-text-primary">:</div>

      {/* Minute Picker */}
      <div className="flex flex-col items-center">
        <div className="text-xs text-text-secondary mb-2">Min</div>
        <div className="relative">
          <select
            value={displayMinute}
            onChange={(e) => handleMinuteChange(Number(e.target.value))}
            className="appearance-none bg-transparent text-2xl font-light text-center w-16 focus:outline-none cursor-pointer"
            style={{ textAlignLast: 'center' }}
          >
            {Array.from({ length: 60 }, (_, i) => i).map(minute => (
              <option key={minute} value={minute}>
                {minute.toString().padStart(2, '0')}
              </option>
            ))}
          </select>
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="border-t border-b border-primary-200 w-full h-8 absolute"></div>
          </div>
        </div>
      </div>

      {/* Period Picker */}
      <div className="flex flex-col items-center">
        <div className="text-xs text-text-secondary mb-2">&nbsp;</div>
        <div className="relative">
          <select
            value={period}
            onChange={(e) => handlePeriodChange(e.target.value)}
            className="appearance-none bg-transparent text-2xl font-light text-center w-16 focus:outline-none cursor-pointer"
            style={{ textAlignLast: 'center' }}
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="border-t border-b border-primary-200 w-full h-8 absolute"></div>
          </div>
        </div>
      </div>
    </div>
  );
}